import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ReactComponent as TruckIllustration } from 'assets/illustrations/TruckIllustration.svg'
import omit from 'lodash/omit'
import { Button } from 'transfix-ui/components/Button'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Toast, ToastVariation } from 'transfix-ui/components/Toast'
import { Typography } from 'transfix-ui/core'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { ICarrierMatchesFilters } from 'apps/booking/types'
import {
  matchesRequestStateSelector,
  matchListSelector,
  matchTotalCountSelector,
} from 'apps/booking/utils/selectors/carrierMatches'
import { activeRowIdSelector } from 'apps/booking/utils/selectors/sidebar'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { DownloadIcon } from 'transfix-icons'
import { MatchRow } from './MatchRow'

interface IMatchListProps {
  filters: Partial<ICarrierMatchesFilters>
  refreshList: () => void
}

export const MatchList = memo<IMatchListProps>(({ filters, refreshList }) => {
  const { trackEvent } = useMPAnalytics()
  const { loading } = useSelector(matchesRequestStateSelector)
  const { result: results } = useSelector(matchListSelector)
  const { totalCount } = useSelector(matchTotalCountSelector)
  const { activeRowId } = useSelector(activeRowIdSelector)
  const [toastMessage, setToastMessage] = useState('')
  const [toastVariant, setToastVariant] = useState<ToastVariation>('default')
  const containsResults = results && results?.length > 0

  const successToast = () => setToastMessage('File will be sent via Slack shortly!')
  const failToast = (err: string) => {
    setToastVariant('alert')
    setToastMessage(err)
  }
  const clearToast = () => {
    setToastVariant('default')
    setToastMessage('')
  }

  const requestCSV = () => {
    const requestData = {
      endpoint: ENDPOINTS.laneMatchTriggers,
      group: 'export_shipment_carrier_list',
      params: {
        shipmentId: activeRowId,
        allowStagingSlack: true,
        ...omit(filters, ['pageCount', 'pageSize']),
      },
    }
    trackEvent('Booking', 'CLICKED_DOWNLOAD_CSV', { shipmentId: activeRowId, ...filters })
    callApi(requestData).then(successToast).catch(failToast)
  }

  return (
    <>
      {containsResults && !loading && (
        <div className='flex justify-between'>
          <div className='pa4 mt2 mb4'>
            <span className='b'>
              {totalCount} carrier{totalCount !== 1 ? 's' : ''}
            </span>
            <span
              className='ml2 btn-text-link cursor'
              role='button'
              onKeyDown={refreshList}
              onClick={refreshList}
              tabIndex={0}>
              Refresh List
            </span>
          </div>
          <div className='pa4'>
            <Button startIcon={<DownloadIcon />} onClick={requestCSV} data-test='edit-post'>
              Download CSV
            </Button>
          </div>
        </div>
      )}
      <div className='mb5'>
        {loading && (
          <div className='pa4 mv6'>
            <Spinner position='relative' />
          </div>
        )}
        {containsResults &&
          results?.map((result, idx) => {
            if (result) {
              return (
                <>
                  <MatchRow match={result} key={result.carrierId} />
                  {results.length !== idx && <hr className='bg-disabled mh0' />}
                </>
              )
            }
            return undefined
          })}
        {!loading && !containsResults && (
          <div className='tc mt6'>
            <TruckIllustration />
            <Typography variant='h2' className='mt4'>
              No matching carriers found
            </Typography>
            <div>Try adjusting search and filter options</div>
          </div>
        )}
      </div>
      <Toast
        open={!!toastMessage}
        onClose={clearToast}
        message={toastMessage}
        duration={5000}
        variation={toastVariant}
      />
    </>
  )
})
